import type { CourseGeometry, HoleGeometry, HoleScore, LatLng, Shot } from './golfRound'

/**
 * A hole, drawn flat: the distance math the club already runs, plus the one
 * projection that turns a course's coordinates into an engraved plan.
 *
 * PURE — no React, no DOM. `components/golf/HolePlan.tsx` is the only thing
 * that knows this produces SVG.
 *
 * ## Why this file measures the way it does
 *
 * `metres`, `yards`, `bearing`, `isInside` and `legsFor` are PORTED, not
 * re-derived, from `apps/caddie/src/lib/geo.ts` — which is itself the client
 * half of `Fireside::Golf::Tracking` (`engines/fireside/app/services/fireside/
 * golf/tracking.rb`). That file's header states the doctrine and it now binds
 * three implementations rather than two: *"the two must agree leg for leg and
 * yard for yard … a change to either shape belongs in both files or in
 * neither."* Two sources of truth for one yardage is how a phone and a
 * scorecard start disagreeing in front of a member; three would be worse. So
 * every number here is arithmetic the server would produce, fed by the scale
 * the server sends with the geometry.
 *
 * A saved `first_putt_origin` closes the terminal approach exactly as the
 * phone's `ballOnGreen` does. It is an endpoint, never another Shot: the line
 * reaches the green while its strike marks still come only from real Shot
 * rows. Where positive putts prove the ball reached a mapped green but no
 * origin was uploaded, the web may extend the line to `hole.pin`; that
 * display-only segment never becomes a measured leg, Shot, club or yardage.
 *
 * ## Why the projection is equirectangular on the SERVER's scale
 *
 * The scale is `[metres per degree of latitude, metres per degree of
 * longitude]`, precomputed at the course's own latitude and shipped with the
 * geometry (`golf_geometry_json`). Multiplying each coordinate by its own
 * constant is a positive diagonal linear map, which is what makes the drawing
 * and the yardages the same arithmetic. The two alternatives are both wrong
 * here for reasons worth writing down rather than rediscovering:
 *
 *   * **Never haversine.** It models a sphere the earth is not, and reads
 *     ~0.25% short as a systematic bias rather than noise — and it is a
 *     DISTANCE function anyway, with no plane to draw on.
 *   * **Never Mercator (nor any web-map tiling projection).** Mercator's
 *     scale factor varies with latitude, so a hole drawn in it is stretched
 *     north-south against its own yardages; a 400-yard drive and a 400-yard
 *     approach pointing different ways would draw different lengths. At golf
 *     scale the local plane is exact to well under a millimetre, and it is the
 *     frame the coordinates were extracted against.
 *
 * That last rule governs THE PAPER PLAN — this file's projection, which is the
 * fallback and the thumbnails. The club's live round-page map (`lib/holeMap.ts`
 * and `components/golf/HoleMap.tsx`) is a map, so it draws in the map's own
 * projection over the tiles; its YARDAGES still come from the walk above and
 * never from measuring the picture, which is what keeps the two surfaces
 * quoting one number.
 */

/**
 * Exported so anything formatting a distance uses the SAME factor this file
 * measures with. Two copies of 1.0936133 disagree the day one is corrected.
 */
export const YARDS_PER_METRE = 1.0936133

/** Metres between two coordinates, on the course's own scale. */
export function metres(from: LatLng, to: LatLng, scale: [number, number]): number {
  const [metresPerDegreeLat, metresPerDegreeLng] = scale
  return Math.hypot((to[0] - from[0]) * metresPerDegreeLat, (to[1] - from[1]) * metresPerDegreeLng)
}

/**
 * WHOLE yards, always, and `Math.round` specifically — `Geo.yards` on the
 * server is `(metres * YARDS_PER_METRE).round`, and Ruby's `.round` and JS's
 * `Math.round` agree on every non-negative half. A distance is never negative,
 * so the two never disagree by a yard.
 */
export function yards(from: LatLng, to: LatLng, scale: [number, number]): number {
  return Math.round(metres(from, to, scale) * YARDS_PER_METRE)
}

/**
 * The bearing from one point to another, in degrees clockwise from north.
 *
 * Used to ROTATE the plan so the hole runs up the box — tee at the bottom,
 * green at the top, which is how a golfer already pictures a hole and how the
 * phone's map orients. Never north-up.
 */
export function bearing(from: LatLng, to: LatLng, scale: [number, number]): number {
  const north = (to[0] - from[0]) * scale[0]
  const east = (to[1] - from[1]) * scale[1]
  return (Math.atan2(east, north) * 180) / Math.PI
}

/**
 * Whether a point falls inside a polygon — a green, in practice, which is what
 * tells a putt from a shot.
 *
 * Even-odd rather than a winding number because a green's outline is a simple,
 * non-self-intersecting ring — where the two agree — and even-odd needs no
 * promise about which way round the coordinates were extracted.
 *
 * Cast in DEGREES deliberately, with no scale argument: the projection is a
 * positive diagonal linear map, which takes lines to lines and preserves which
 * side of one a point falls on, so the crossing count is identical in either
 * frame. `Geo.inside?` makes the same argument for the same reason.
 *
 * Fewer than three vertices is not a polygon — a green that came back as a
 * point or a line has no inside, and inventing one would quote a fairway shot
 * as a putt.
 */
export function isInside(point: LatLng, polygon: LatLng[] | undefined): boolean {
  if (polygon === undefined || polygon.length < 3) return false

  const [lat, lng] = point
  let inside = false
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index, index += 1) {
    const here = polygon[index]!
    const there = polygon[previous]!
    // Only edges that straddle the ray's latitude can cross it. The strict
    // inequality on exactly one side is what keeps a vertex ON the ray from
    // being counted twice.
    if (here[0] > lat === there[0] > lat) continue

    const crossingLng = here[1] + ((lat - here[0]) / (there[0] - here[0])) * (there[1] - here[1])
    if (lng < crossingLng) inside = !inside
  }
  return inside
}

/** The fields a leg is measured from — a strike point and the club that left it. */
export type StruckShot = Pick<Shot, 'lat' | 'lng' | 'club'>

/** The same, plus the sequence a strike mark is labelled and ordered by. */
export type TracedShot = Pick<Shot, 'lat' | 'lng' | 'club' | 'sequence'>

/**
 * One drawn leg of a hole: where the stroke was played from, where it finished,
 * and how far it played. Mirrors `Golf::Tracking.trace`.
 *
 * ⚠️ **A shot's position is where it was STRUCK FROM**, so a leg spans two
 * strike points and its length is how far the FIRST of them travelled. Nothing
 * is inferred from the mapped tee: both ends are fixes the member stood on,
 * which is why no leg is "approximate" and there is no leg at all before the
 * first shot.
 */
export interface Leg {
  /** The strike point of the shot that hit this leg. */
  from: LatLng
  /**
   * Where that ball came to rest — known because the member either struck
   * their NEXT shot there or saved the origin of their first putt.
   */
  to: LatLng
  /** WHOLE yards, exactly as `Golf::Tracking.trace` would quote them. */
  yards: number
  /**
   * The same leg, unrounded, in metres. Both exist because they answer
   * different questions: `yards` is a display integer that must agree with the
   * server, `metres` is the measurement it was rounded from. Deriving one from
   * the other quantizes through a number already rounded to the nearest yard.
   */
  metres: number
  /**
   * The club that HIT this leg — the club of the shot at its START.
   *
   * ⚠️ The far end's club is the one played NEXT, off the ball this leg
   * delivered. Taking it from there labels every distance with the following
   * stroke's club, which is wrong in a way that reads entirely plausible: a
   * drive's 248 yards wearing the 8 iron that came after it.
   */
  club: string | null
}

/**
 * The played legs of one hole. Shots must already be in sequence order —
 * `shotsForHole` below is what guarantees that.
 *
 * A shot is a STRIKE POINT, so the ordinary legs are gaps between consecutive
 * shots and there is one FEWER leg than there are shots. A saved first-putt
 * origin may add exactly one terminal leg, making the counts equal while the
 * marks remain one per real Shot.
 *
 * Empty without a projection: an unmapped course sends no scale, and a trace
 * with no distances on it is not this function's to invent. `Tracking.legs`
 * returns `[]` in the same case and for the same reason — a list in which
 * every entry says nothing is not a partial answer, it is an empty one wearing
 * the shape of an answer.
 */
export function legsFor(
  shots: readonly StruckShot[],
  hole: HoleGeometry | undefined,
  scale: [number, number] | undefined,
  firstPuttOrigin: HoleScore['first_putt_origin'] = null,
): Leg[] {
  if (scale === undefined) return []

  const legs: Leg[] = []
  for (let index = 0; index < shots.length - 1; index += 1) {
    const struck = shots[index]!
    const from: LatLng = [struck.lat, struck.lng]
    // ⚠️ ONLY SHOTS ARE DRAWN, NEVER PUTTS, and the walk is a PREFIX —
    // `take_while`, never `reject`. A stroke played from inside the green is a
    // putt: it belongs to the putts count, and drawing it here would run the
    // trace across a surface where the interesting distances are feet.
    //
    // The cost is named in `tracking.rb` and in caddie's `geo.ts`, and it is
    // the same cost here: a ball that comes back off a green (a putt through
    // the fringe, a chip from the collar a ±3-5 m fix reads as inside) takes
    // everything after it out of the drawing. Skipping one leg and carrying on
    // is worse — `legs[i]` is `shots[i]`'s own leg, and every consumer depends
    // on that alignment, so a shortened-in-the-middle array puts a member's
    // club on the wrong distance. A hole is played tee to green in one
    // direction; a prefix is the honest shape. The day this must survive a
    // ball coming back, the fix is a per-leg `putt` flag on an index-aligned
    // array, in all three files together.
    if (isInside(from, hole?.green)) return legs

    const next = shots[index + 1]!
    const to: LatLng = [next.lat, next.lng]
    legs.push({
      from,
      to,
      yards: yards(from, to, scale),
      metres: metres(from, to, scale),
      club: struck.club,
    })
  }

  // The score confirms where the LAST real shot finished. That gives the
  // approach its missing far end, but does not manufacture a Shot mark for a
  // putt. A legacy payload may already include a Shot struck from inside the
  // green; in that shape the ordinary consecutive-shot leg is the endpoint and
  // appending this one would draw it twice.
  if (firstPuttOrigin !== null && hasTerminalApproach(shots, hole, firstPuttOrigin)) {
    const struck = shots.at(-1)!
    const from: LatLng = [struck.lat, struck.lng]
    const to: LatLng = [firstPuttOrigin.lat, firstPuttOrigin.lng]
    legs.push({
      from,
      to,
      yards: yards(from, to, scale),
      metres: metres(from, to, scale),
      club: struck.club,
    })
  }

  return legs
}

/** Whether a saved first-putt position supplies one unmarked terminal leg. */
export function hasTerminalApproach(
  shots: readonly StruckShot[],
  hole: HoleGeometry | undefined,
  firstPuttOrigin: HoleScore['first_putt_origin'] = null,
): boolean {
  return (
    shots.length > 0 &&
    firstPuttOrigin !== null &&
    !shots.some((shot) => isInside([shot.lat, shot.lng], hole?.green))
  )
}

export type TraceFinish = 'recorded' | 'mapped-center' | 'partial'

export interface ResolvedTraceFinish {
  finish: TraceFinish
  /** The unmarked coordinate that closes this trace, when one is available. */
  endpoint: LatLng | null
}

/** Classify how the visible trace finishes, without manufacturing a measured leg. */
export function resolveTraceFinish(
  shots: readonly StruckShot[],
  hole: HoleGeometry | undefined,
  score: HoleScore | null | undefined,
): ResolvedTraceFinish {
  const firstPuttOrigin = score?.first_putt_origin ?? null
  if (firstPuttOrigin !== null) {
    return {
      finish: 'recorded',
      endpoint: hasTerminalApproach(shots, hole, firstPuttOrigin)
        ? [firstPuttOrigin.lat, firstPuttOrigin.lng]
        : null,
    }
  }

  if (shots.some((shot) => isInside([shot.lat, shot.lng], hole?.green))) {
    return { finish: 'recorded', endpoint: null }
  }

  if (
    hole !== undefined &&
    (score?.putts ?? 0) > 0 &&
    shots.some((shot) => !isInside([shot.lat, shot.lng], hole.green))
  ) {
    return { finish: 'mapped-center', endpoint: hole.pin }
  }

  return { finish: 'partial', endpoint: null }
}

/**
 * One player's shots for one hole, in the order they were played.
 *
 * A round player's `shots` arrive FLAT across every hole (`round_player_json`
 * — deliberately unnested, so a hole with shots and no score is expressible),
 * so filtering is the drawing's job. The sort is defensive rather than
 * corrective: the server orders by sequence, and a JSON array's order is not
 * a promise the walk should bet its leg alignment on.
 */
export function shotsForHole<T extends { hole_number: number; sequence: number }>(
  shots: readonly T[],
  holeNumber: number,
): T[] {
  return shots.filter((shot) => shot.hole_number === holeNumber).sort((a, b) => a.sequence - b.sequence)
}

/** The hole in a course's geometry, or undefined — 7 of the 15 courses are unmapped. */
export function holeIn(
  geometry: CourseGeometry | null | undefined,
  holeNumber: number,
): HoleGeometry | undefined {
  return geometry?.holes.find((hole) => hole.number === holeNumber)
}

/**
 * The cheap guard a caller branches on before rendering a plan at all.
 *
 * The degradation matrix's last row is "no geometry ⇒ no SVG and an honest
 * line" — and it is the CALLER's row, not the component's: a `HolePlan` that
 * rendered an empty frame would still spend the vertical space and still
 * announce itself to a screen reader. So `HolePlan` requires a hole and a
 * scale, and this is how a screen finds out whether it has them.
 */
export function hasGeometry(
  geometry: CourseGeometry | null | undefined,
  holeNumber: number,
): boolean {
  return holeIn(geometry, holeNumber) !== undefined
}

// ── The projection ─────────────────────────────────────────────────────────

/** A point in the plan's own user units — SVG's frame, y growing downward. */
export interface PlanPoint {
  x: number
  y: number
}

/** The drawing surface: user units, and the margin no geometry may enter. */
export interface ProjectionBox {
  width: number
  height: number
  /** Kept clear on every side. Defaults to `DEFAULT_PADDING`. */
  padding?: number
}

export interface HoleProjection {
  /** Any coordinate on this course, placed in the box. */
  toPoint(point: LatLng): PlanPoint
  /** `"0 0 w h"` — pair it with `preserveAspectRatio="xMidYMid meet"`. */
  viewBox: string
  /**
   * How many metres one user unit is. The fit is UNIFORM, so this is a single
   * number in both axes — which is the whole point of fitting uniformly, and
   * what lets a caller size a mark or a hairline against real ground.
   */
  metresPerUnit: number
}

/**
 * Room for the tee square, the pin's flag and a strike mark's ring to sit
 * fully inside the frame rather than half-clipped by the viewBox.
 */
const DEFAULT_PADDING = 14

function isFinitePoint(point: LatLng | undefined | null): point is LatLng {
  return (
    Array.isArray(point) &&
    point.length === 2 &&
    Number.isFinite(point[0]) &&
    Number.isFinite(point[1])
  )
}

/**
 * Everything a hole is drawn from, as one list — what the fit must contain.
 * Hazard centroids are in it because a bunker imported from OSM is a single
 * node with no polygon, and a blob drawn off the edge of the frame is a blob
 * nobody sees.
 */
export function holePoints(hole: HoleGeometry): LatLng[] {
  const points: LatLng[] = [hole.tee, hole.pin, ...hole.path, ...hole.green]
  for (const hazard of hole.hazards ?? []) {
    points.push(hazard.point)
    if (hazard.polygon) points.push(...hazard.polygon)
  }
  for (const tee of hole.tees ?? []) points.push(tee.point)
  if (hole.outline) points.push(...hole.outline)
  for (const fairway of hole.fairways ?? []) points.push(...fairway)
  return points.filter(isFinitePoint)
}

/**
 * The hole, flattened and framed.
 *
 * Three things happen, in this order, and each one has a reason:
 *
 * 1. **Local equirectangular about the hole's centroid, on the SERVER's
 *    scale** — see the module header. The centroid is the mean of the hole's
 *    own points and is a numerical convenience only: an origin is a
 *    translation, and a translation before a linear map and a bounding-box fit
 *    cancels out entirely. It is chosen so the local metre values stay small
 *    near the thing being drawn rather than carrying the course's whole offset.
 *
 * 2. **Rotated by −`bearing(tee, pin)`**, which sends the tee→pin direction to
 *    due north and therefore up the box. A portrait plan of a hole that ran
 *    east-west would otherwise be a hole drawn across a sliver.
 *
 * 3. **Fitted UNIFORMLY inside `box` with `padding` kept clear.** Uniform
 *    because a non-uniform fit would make the same yardage draw two lengths
 *    depending on which way it pointed — the exact defect that rules Mercator
 *    out. The fit is centred, so a hole shorter than its frame sits in the
 *    middle of it rather than in a corner.
 *
 * `extraPoints` is for coordinates that are not part of the course file but
 * must still land inside the frame — strike points and applicable terminal
 * endpoints. Either can finish outside every polygon the hole has, and an SVG
 * root clips: without this it would simply not be drawn, which is the one
 * degradation this component must never do silently.
 */
export function projectHole(
  hole: HoleGeometry,
  scale: [number, number],
  box: ProjectionBox,
  extraPoints: readonly LatLng[] = [],
): HoleProjection {
  const padding = box.padding ?? DEFAULT_PADDING
  const fitted = [...holePoints(hole), ...extraPoints.filter(isFinitePoint)]

  const origin = centroidOf(fitted.length > 0 ? fitted : [hole.tee])
  const radians = (bearing(hole.tee, hole.pin, scale) * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  // (across, along) metres: `along` runs tee→pin, `across` runs right of it.
  const rotate = (point: LatLng): { across: number; along: number } => {
    const north = (point[0] - origin[0]) * scale[0]
    const east = (point[1] - origin[1]) * scale[1]
    return { across: east * cos - north * sin, along: north * cos + east * sin }
  }

  const rotated = fitted.map(rotate)
  const acrossValues = rotated.map((point) => point.across)
  const alongValues = rotated.map((point) => point.along)
  const minAcross = acrossValues.length > 0 ? Math.min(...acrossValues) : 0
  const maxAcross = acrossValues.length > 0 ? Math.max(...acrossValues) : 0
  const minAlong = alongValues.length > 0 ? Math.min(...alongValues) : 0
  const maxAlong = alongValues.length > 0 ? Math.max(...alongValues) : 0

  const usableWidth = Math.max(0, box.width - padding * 2)
  const usableHeight = Math.max(0, box.height - padding * 2)
  // A hole with no extent in one axis is a legitimate degenerate — a par 3
  // whose tee and pin share a longitude has none across — and Infinity loses
  // the `min`, which is what makes the other axis decide. Both degenerate (one
  // repeated coordinate) has no scale to derive, so the point simply lands in
  // the middle of the box.
  const acrossUnits = maxAcross > minAcross ? usableWidth / (maxAcross - minAcross) : Infinity
  const alongUnits = maxAlong > minAlong ? usableHeight / (maxAlong - minAlong) : Infinity
  const candidate = Math.min(acrossUnits, alongUnits)
  const unitsPerMetre = Number.isFinite(candidate) && candidate > 0 ? candidate : 1

  const midAcross = (minAcross + maxAcross) / 2
  const midAlong = (minAlong + maxAlong) / 2
  const centreX = box.width / 2
  const centreY = box.height / 2

  return {
    toPoint(point: LatLng): PlanPoint {
      const { across, along } = rotate(point)
      return {
        x: centreX + (across - midAcross) * unitsPerMetre,
        // MINUS: SVG's y grows downward and `along` grows toward the pin, so
        // the tee lands BELOW the green — which is the whole point of the
        // rotation above.
        y: centreY - (along - midAlong) * unitsPerMetre,
      }
    },
    viewBox: `0 0 ${box.width} ${box.height}`,
    metresPerUnit: 1 / unitsPerMetre,
  }
}

function centroidOf(points: readonly LatLng[]): LatLng {
  let lat = 0
  let lng = 0
  for (const point of points) {
    lat += point[0]
    lng += point[1]
  }
  return [lat / points.length, lng / points.length]
}

// ── The trace ──────────────────────────────────────────────────────────────

/** A strike point, drawn: where the member stood, and which stroke it was. */
export interface TraceMark {
  sequence: number
  point: PlanPoint
  /** The club they had in hand there. Null is normal. */
  club: string | null
}

/** One leg, placed — the same numbers as `Leg`, in the plan's own units. */
export interface TraceLeg {
  from: PlanPoint
  to: PlanPoint
  yards: number
  metres: number
  club: string | null
}

export interface HoleTrace {
  /**
   * ONE polyline through every known point, in order — real strike points plus
   * an optional unmarked recorded or mapped endpoint. A mapped-center endpoint
   * extends only this line; it never appears in `legs`. Never one line per leg:
   * a shared endpoint drawn twice puts two round caps on top of each other and
   * every bounce reads as a blob.
   *
   * Empty for a hole with fewer than two drawn points, which a caller renders
   * as NO polyline at all rather than an empty one.
   */
  polyline: PlanPoint[]
  /** One per drawn strike point — including the single shot that has no leg. */
  marks: TraceMark[]
  legs: TraceLeg[]
  finish: TraceFinish
}

const NOTHING_DRAWN: HoleTrace = { polyline: [], marks: [], legs: [], finish: 'partial' }

/**
 * One player's hole, ready to draw: the straight chord through every known
 * point, a mark at each real strike point, and the legs with their yardages.
 *
 * **Straight chords, and that is a decision rather than an omission.** The
 * phone bows each leg (`arcBetween`) because its legs are drawn over satellite
 * imagery at a zoom where two shots down the same line collapse into one
 * stroke, and because a curve gives a yardage pill a shoulder to sit on. This
 * plan is an ENGRAVING on paper at a fixed size, with the whole hole in frame
 * and at most four players on it: a bow would be four decorative curves
 * competing with the hole's own centre line, and it would state a shot shape
 * the club does not know. A chord says exactly what is known — struck here,
 * finished there — and nothing more.
 *
 * The marks are the drawn PREFIX of the shots, so they stop where `legsFor`
 * stops: ordinarily `legs.length + 1`, or `legs.length` when the final leg is
 * the unmarked first-putt endpoint. One shot without any finish is therefore
 * one mark and no line; positive putts on a mapped hole give that mark a
 * line-only endpoint while leaving `legs` empty.
 */
export function holeTrace(
  shots: readonly TracedShot[],
  hole: HoleGeometry | undefined,
  scale: [number, number] | undefined,
  projection: HoleProjection,
  score?: HoleScore,
): HoleTrace {
  if (scale === undefined || shots.length === 0) return NOTHING_DRAWN

  const firstPuttOrigin = score?.first_putt_origin ?? null
  const legs = legsFor(shots, hole, scale, firstPuttOrigin)
  const hasTerminalLeg = hasTerminalApproach(shots, hole, firstPuttOrigin)
  const resolvedFinish = resolveTraceFinish(shots, hole, score)
  const drawn = shots.slice(0, Math.min(legs.length + 1 - (hasTerminalLeg ? 1 : 0), shots.length))
  const marks = drawn.map((shot) => ({
    sequence: shot.sequence,
    point: projection.toPoint([shot.lat, shot.lng]),
    club: shot.club,
  }))
  const measuredPolyline =
    legs.length > 0
      ? [projection.toPoint(legs[0]!.from), ...legs.map((leg) => projection.toPoint(leg.to))]
      : []
  const polyline =
    resolvedFinish.finish === 'mapped-center' && resolvedFinish.endpoint !== null
      ? [
          ...(measuredPolyline.length > 0 ? measuredPolyline : [marks[0]!.point]),
          projection.toPoint(resolvedFinish.endpoint),
        ]
      : measuredPolyline

  return {
    polyline,
    marks,
    legs: legs.map((leg) => {
      const from = projection.toPoint(leg.from)
      const to = projection.toPoint(leg.to)
      return {
        from,
        to,
        yards: leg.yards,
        metres: leg.metres,
        club: leg.club,
      }
    }),
    finish: resolvedFinish.finish,
  }
}

/**
 * A list of plan points as SVG's `points` attribute takes them.
 *
 * Two decimals: at the sizes these plans render, a hundredth of a user unit is
 * far under a printed pixel, and full float expansion triples the markup of a
 * fourball's four traces for nothing.
 */
export function polylinePoints(points: readonly PlanPoint[]): string {
  return points.map((point) => `${round2(point.x)},${round2(point.y)}`).join(' ')
}

/** A ring or a path as SVG `d`, closed when it is a ring. */
export function pathFrom(points: readonly PlanPoint[], options: { close?: boolean } = {}): string {
  if (points.length === 0) return ''
  const [first, ...rest] = points
  const body = rest.map((point) => `L${round2(point.x)} ${round2(point.y)}`).join(' ')
  return `M${round2(first!.x)} ${round2(first!.y)}${body ? ` ${body}` : ''}${options.close ? ' Z' : ''}`
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}
